<template>
  <div class="offer-configure">
    <b-alert v-if="!canView" variant="warning" show class="mb-1 py-50">
      You do not have permission to view offer letter configurations.
    </b-alert>

    <template v-else>
      <offer-configure-list-toolbar
        :can-manage="canManage"
        :loading="loading"
        :search-query.sync="searchQuery"
        :show-inactive="showInactive"
        :total-count="configs.length"
        :filtered-count="filteredConfigs.length"
        :active-count="activeConfigCount"
        :inactive-count="inactiveConfigCount"
        @create="openCreateForm"
        @refresh="loadConfigs"
        @show-inactive-change="onShowInactiveChange"
      />

      <offer-configure-list-table
        :items="filteredConfigs"
        :table-fields="tableFields"
        :loading="loading"
        :load-error="loadError"
        :can-manage="canManage"
        :has-list-filters="hasListFilters"
        @create="openCreateForm"
        @edit="openEditForm"
        @replicate="replicateConfig"
        @toggle-status="toggleTemplateStatus"
        @delete="confirmDelete"
        @clear-filters="clearListFilters"
      />

      <!-- Create / Edit wizard modal -->
      <b-modal
        v-model="showFormModal"
        :title="formModalTitle"
        size="xl"
        scrollable
        modal-class="offer-configure-modal"
        dialog-class="offer-configure-modal-dialog"
        content-class="offer-configure-modal-content"
        body-class="p-0"
        @hidden="resetForm"
      >
        <!-- Wizard progress header -->
        <div class="oc-wizard-header px-2 pt-1 pb-75 border-bottom">
          <offer-configure-wizard-nav
            :steps="wizardStepLabels"
            :current-step="wizardStep"
            :completion-percent="wizardCompletionPercent"
            :can-go-to="canGoToWizardStep"
            @select="goWizardStep"
          />
          <div
            v-if="wizardStep === 3"
            class="oc-section-chip-row d-flex flex-wrap align-items-center mt-75"
          >
            <span class="oc-section-chip-label text-muted small mr-50 mb-25">
              <feather-icon icon="LayersIcon" size="12" class="mr-25" />
              In letter:
            </span>
            <b-badge
              v-for="chip in letterSectionChips"
              :key="chip.key"
              :variant="chip.included ? 'light-success' : 'light-secondary'"
              class="oc-section-chip mr-25 mb-25"
            >
              {{ chip.label }}
              <span class="oc-section-chip-state">{{ chip.included ? 'on' : 'off' }}</span>
            </b-badge>
          </div>

          <b-alert
            v-if="isEditing"
            show
            variant="primary"
            class="oc-edit-banner py-75 mt-75 mb-0"
          >
            <div class="d-flex align-items-start">
              <feather-icon icon="EditIcon" size="16" class="mr-50 mt-25 flex-shrink-0" />
              <div class="small mb-0">
                <strong>Editing template for {{ editBannerSite }} · {{ editBannerClient }}.</strong>
                Site identity is locked. Update letter content, salary, and branding below.
              </div>
            </div>
          </b-alert>
        </div>

        <b-form class="oc-wizard-body px-2 py-1" @submit.prevent="saveConfig">
          <div v-if="formLoading" class="oc-form-loading text-center py-5">
            <b-spinner variant="primary" />
            <div class="mt-1 text-muted small">Loading template…</div>
          </div>

          <template v-else>
            <!-- Mobile: collapsible live preview above the form -->
            <div class="d-lg-none mb-1">
              <b-button
                block
                size="sm"
                variant="outline-primary"
                class="oc-touch-btn"
                @click="showMobilePreview = !showMobilePreview"
              >
                <feather-icon
                  :icon="showMobilePreview ? 'ChevronUpIcon' : 'EyeIcon'"
                  size="14"
                  class="mr-50"
                />
                {{ showMobilePreview ? 'Hide' : 'Show' }} Live Preview
              </b-button>
              <b-collapse v-model="showMobilePreview" class="mt-1">
                <offer-configure-live-preview
                  :key="livePreviewMountKey"
                  :srcdoc="livePreviewSrcdoc"
                  :empty-srcdoc="emptyPreviewSrcdoc"
                  :has-content="hasPreviewContent"
                  :updating="previewUpdating"
                />
              </b-collapse>
            </div>

            <b-row class="oc-wizard-split">
              <!-- Desktop: sticky live preview on the LEFT -->
              <b-col cols="12" lg="6" class="d-none d-lg-block mb-lg-0">
                <div class="oc-live-preview-sticky">
                  <div class="oc-live-preview-scroll">
                    <offer-configure-live-preview
                      :key="livePreviewMountKey"
                      :srcdoc="livePreviewSrcdoc"
                      :empty-srcdoc="emptyPreviewSrcdoc"
                      :has-content="hasPreviewContent"
                      :updating="previewUpdating"
                    />
                  </div>
                </div>
              </b-col>

              <b-col cols="12" lg="6" class="oc-wizard-form-col">
                <!-- Step 0: Basic Info -->
                <offer-configure-step-site
                  v-show="wizardStep === 0"
                  :form="form"
                  :can-manage="canManage"
                  :is-editing="isEditing"
                  :is-site-code-disabled="isSiteCodeDisabled"
                  :is-site-name-disabled="isSiteNameDisabled"
                  :is-client-disabled="isClientDisabled"
                  :is-location-disabled="isLocationDisabled"
                  @site-name-edited="onSiteNameEdited"
                />

                <!-- Step 1: Letter Content -->
                <offer-configure-step-letter
                  v-show="wizardStep === 1"
                  :template-editor="templateEditor"
                  :letter-section-picker-items="letterSectionPickerItems"
                  :included-ordered-letter-sections="includedOrderedLetterSections"
                  :active-letter-section="activeLetterSection"
                  :letter-title-text.sync="letterTitleText"
                  :layout-defaults="layoutDefaults"
                  :font-size-select-options="fontSizeSelectOptions"
                  :title-align-options="titleAlignOptions"
                  :default-section-title-text="defaultSectionTitleText"
                  :show-letter-settings-modal.sync="showLetterSettingsModal"
                  :offer-signature-locations-model="offerSignatureLocationsModel"
                  :employee-sign-placement="employeeSignPlacement"
                  :editor-mount-key="editorMountKey"
                  :form-id="form.id"
                  :editor-disabled="!canManage || templateEditor.mode === 'raw'"
                  :section-layout-resolved="sectionLayoutResolved"
                  :section-page-start-mode="sectionPageStartMode"
                  :letter-accordion-id="letterAccordionId"
                  @toggle-section="toggleLetterSection"
                  @open-section="openLetterSection"
                  @show-all-sections="showAllLetterSections"
                  @letter-title-align-change="onLetterTitleAlignChange"
                  @section-layout-change="onSectionLayoutChange"
                  @layout-default-change="onLayoutDefaultChange"
                  @signature-locations-change="onSignaturePlacementChange"
                  @employee-sign-change="onEmployeeSignPlacementChange"
                  @accordion-header-click="onLetterAccordionHeaderClick"
                  @move-section="moveLetterSection"
                  @page-start-change="onPageStartModeChange"
                  @section-title-change="onSectionTitleTextChange"
                  @section-title-font-size-change="onSectionTitleFontSizeChange"
                  @section-title-font-weight-change="onSectionTitleFontWeightChange"
                  @section-title-align-change="onSectionTitleAlignChange"
                  @letter-field-input="onLetterFieldInput"
                  @sync-template-from-fields="syncTemplateFromFields"
                />

                <!-- Step 2: Salary & Branding -->
                <offer-configure-step-salary-branding
                  v-show="wizardStep === 2"
                  :salary-section-included="salarySectionIncluded"
                  :salary-parse-warning="salaryParseWarning"
                  :salary-editor="salaryEditor"
                  :active-structure-index="activeSalaryStructureIndex"
                  :default-section-title-text="defaultSectionTitleText"
                  :editor-disabled="!canManage || templateEditor.mode === 'raw'"
                  :section-layout-resolved="sectionLayoutResolved"
                  :section-page-start-mode="sectionPageStartMode"
                  :form="form"
                  :can-manage="canManage"
                  :uploaded-image-count="uploadedImageCount"
                  :image-keys="imageKeys"
                  :image-labels="imageLabels"
                  :image-hints="imageHints"
                  :image-icons="imageIcons"
                  @page-start-change="onPageStartModeChange"
                  @section-layout-change="onSectionLayoutChange"
                  @section-title-change="onSectionTitleTextChange"
                  @section-title-font-size-change="onSectionTitleFontSizeChange"
                  @section-title-font-weight-change="onSectionTitleFontWeightChange"
                  @section-title-align-change="onSectionTitleAlignChange"
                  @salary-input="onSalaryAnnexureInput"
                  @add-row="addSalaryRow"
                  @remove-row="removeSalaryRow"
                  @move-row="moveSalaryRow"
                  @add-structure="addSalaryStructure"
                  @remove-structure="removeSalaryStructure"
                  @active-structure-change="activeSalaryStructureIndex = $event"
                  @image-upload="onImageUpload"
                />

                <!-- Step 3: Preview & Save -->
                <offer-configure-step-review
                  v-show="wizardStep === 3"
                  :form="form"
                  :can-manage="canManage"
                  :is-form-valid="isFormValid"
                  :previewing="previewing"
                  :has-preview-content="hasPreviewContent"
                  :excluded-letter-sections-label="excludedLetterSectionsLabel"
                  :letter-title-text="letterTitleText"
                  :signature-summary-label="signatureSummaryLabel"
                  :included-letter-sections-label="includedLetterSectionsLabel"
                  :salary-row-count="salaryComponentRowCount"
                  :uploaded-image-count="uploadedImageCount"
                  :image-keys="imageKeys"
                  @preview-pdf="previewPdf"
                />
              </b-col>
            </b-row>
          </template>
        </b-form>

        <!-- Sticky wizard footer -->
        <template #modal-footer>
          <div class="oc-wizard-footer w-100 d-flex flex-wrap justify-content-between align-items-center">
            <b-button
              variant="outline-secondary"
              class="oc-touch-btn mb-50 mb-sm-0"
              :disabled="formLoading"
              @click="showFormModal = false"
            >
              Cancel
            </b-button>
            <div class="d-flex flex-wrap align-items-center justify-content-end">
              <b-button
                v-if="wizardStep > 0"
                variant="outline-primary"
                class="oc-touch-btn mr-50 mb-50 mb-sm-0"
                :disabled="formLoading"
                @click="goWizardPrev"
              >
                <feather-icon icon="ChevronLeftIcon" size="15" class="mr-50" />
                Back
              </b-button>
              <b-button
                v-if="wizardStep < wizardStepLabels.length - 1"
                variant="primary"
                class="oc-touch-btn mb-50 mb-sm-0"
                :disabled="formLoading || !canWizardNext"
                @click="goWizardNext"
              >
                Next: {{ wizardStepLabels[wizardStep + 1] }}
                <feather-icon icon="ChevronRightIcon" size="15" class="ml-50" />
              </b-button>
              <template v-else>
                <b-button
                  v-if="canManage && hasPreviewContent"
                  variant="outline-primary"
                  class="oc-touch-btn mr-50 mb-50 mb-sm-0"
                  :disabled="formLoading || previewing"
                  @click="previewPdf"
                >
                  <b-spinner v-if="previewing" small class="mr-50" />
                  <feather-icon v-else icon="FileIcon" size="15" class="mr-50" />
                  Preview PDF
                </b-button>
                <b-button
                  v-if="canManage"
                  type="button"
                  variant="primary"
                  class="oc-touch-btn oc-save-btn"
                  :disabled="formLoading || saving || !isFormValid"
                  @click="saveConfig"
                >
                  <b-spinner v-if="saving" small class="mr-50" />
                  <feather-icon v-else icon="SaveIcon" size="15" class="mr-50" />
                  {{ isEditing ? 'Save Changes' : 'Create Template' }}
                </b-button>
              </template>
            </div>
          </div>
        </template>
      </b-modal>
    </template>
  </div>
</template>

<script>
import {
  BAlert,
  BButton,
  BBadge,
  BSpinner,
  BForm,
  BModal,
  BRow,
  BCol,
  BCollapse,
  VBTooltip,
} from 'bootstrap-vue'
import siteConfigService from '@/services/site-config-service'
import offerLetterService from '@/services/offer-letter-service'
import siteManager from '@/services/site-manager'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  canViewOfferConfigure,
  canManageOfferConfigure,
} from '@/utils/offer-configure-permissions'
import {
  emptyTemplateFields,
  emptySalaryStructureData,
  emptySalaryStructureBlock,
  parseOfferTemplate,
  parseSalaryTableHtml,
  buildSalaryTableHtml,
  assembleOfferTemplate,
  buildDefaultShell,
  repairOfferBodyAfterChrome,
  syncSalaryRowsYearly,
  extractOfferHtmlFromMetadata,
  extractOfferImagesFromMetadata,
  defaultOfferSections,
  OFFER_SECTION_KEYS,
  normalizeOfferSections,
  normalizeSalaryStructureData,
  countSalaryStructureComponentRows,
  defaultSalaryStructureTitle,
  extractOfferSectionDrafts,
  applyOfferSectionDrafts,
  fillStaleSectionDrafts,
  isSectionVisible,
  defaultOfferLayout,
  normalizeOfferLayout,
  resolveSectionLayout,
  applyOfferSignaturePlacementToLayout,
  defaultOfferSectionOrder,
  normalizeOfferSectionOrder,
  extractOfferSalaryComponentOrder,
  normalizeOfferSalaryComponentOrder,
  reorderSalaryRowsByComponentOrder,
  moveSalaryRowOrder,
  OFFER_FONT_SIZE_OPTIONS,
  OFFER_SIGNATURE_PLACEMENT_OPTIONS,
  OFFER_SIGNATURE_PLACEMENT,
  DEFAULT_OFFER_SIGNATURE_LOCATIONS,
  normalizeOfferSignatureLocations,
  resolveOfferSignaturePagesFromMetadata,
  normalizeOfferEmployeeSignConfig,
  resolveOfferEmployeeSignConfigFromMetadata,
  resolveFieldsSignaturePages,
  resolveFieldsEmployeeSignConfig,
  OFFER_PAGE_START_MODE,
  DEFAULT_SECTION_TITLE_TEXT,
  derivePageStartMode,
  applyPageStartModeToSection,
  OFFER_TITLE_ALIGN_OPTIONS,
} from '@/utils/offer-template-editor'
import {
  EMPTY_PREVIEW_SRCDOC,
  IMAGE_KEYS,
  IMAGE_LABELS,
  IMAGE_HINTS,
  IMAGE_ICONS,
  WIZARD_STEP_LABELS,
  LETTER_SECTION_CHIP_DEFS,
  LETTER_CONTENT_SECTION_KEYS,
  LETTER_SECTION_EDITOR_DEFS,
  TABLE_FIELDS,
  MAX_OFFER_CONFIG_PAYLOAD_BYTES,
  MAX_OFFER_CONFIG_FIELD_LENGTH,
} from './offer-configure/constants'
import { emptyForm, buildAutoSiteName, withDefaultOfferBranding } from './offer-configure/form-model'
import {
  parseJson,
  estimateJsonPayloadBytes,
  plainTextSnippet,
  htmlContainsPlainSnippet,
  buildOfferGenerationHtml,
  buildOfferPreviewDocumentHtml,
} from './offer-configure/helpers'
import '@/views/apps/offer/offer-configure/offer-configure-quill'
import OfferConfigureWizardNav from './components/OfferConfigureWizardNav.vue'
import OfferConfigureListToolbar from './components/offer-configure/OfferConfigureListToolbar.vue'
import OfferConfigureListTable from './components/offer-configure/OfferConfigureListTable.vue'
import OfferConfigureStepSite from './components/offer-configure/OfferConfigureStepSite.vue'
import OfferConfigureStepLetter from './components/offer-configure/OfferConfigureStepLetter.vue'
import OfferConfigureStepSalaryBranding from './components/offer-configure/OfferConfigureStepSalaryBranding.vue'
import OfferConfigureStepReview from './components/offer-configure/OfferConfigureStepReview.vue'
import OfferConfigureLivePreview from './components/offer-configure/OfferConfigureLivePreview.vue'

export default {
  name: 'OfferConfigure',
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BAlert,
    BButton,
    BBadge,
    BSpinner,
    BForm,
    BModal,
    BRow,
    BCol,
    BCollapse,
    OfferConfigureWizardNav,
    OfferConfigureListToolbar,
    OfferConfigureListTable,
    OfferConfigureStepSite,
    OfferConfigureStepLetter,
    OfferConfigureStepSalaryBranding,
    OfferConfigureStepReview,
    OfferConfigureLivePreview,
  },
  data() {
    return {
      loading: false,
      formLoading: false,
      saving: false,
      previewing: false,
      previewUpdating: false,
      livePreviewSrcdoc: EMPTY_PREVIEW_SRCDOC,
      previewRefreshTimer: null,
      showMobilePreview: false,
      emptyPreviewSrcdoc: EMPTY_PREVIEW_SRCDOC,
      loadError: '',
      configs: [],
      searchQuery: '',
      showInactive: false,
      showFormModal: false,
      isEditing: false,
      isReplicating: false,
      // Stable id for PUT /offer-table-config/:id — do not rely only on form.id
      // (if form.id is lost mid-edit, save used to POST a duplicate row).
      editingId: null,
      siteNameManuallyEdited: false,
      _syncingSiteName: false,
      form: emptyForm(),
      // Full metadata from last loaded record — preserve version/migratedFrom/extras on save
      existingMetadata: null,
      wizardStep: 0,
      wizardStepLabels: WIZARD_STEP_LABELS,
      maxWizardStepReached: 0,
      imageKeys: IMAGE_KEYS,
      imageLabels: IMAGE_LABELS,
      imageHints: IMAGE_HINTS,
      imageIcons: IMAGE_ICONS,
      activeLetterSection: '',
      showLetterSettingsModal: false,
      titleAlignOptions: OFFER_TITLE_ALIGN_OPTIONS,
      defaultSectionTitleText: DEFAULT_SECTION_TITLE_TEXT,
      salaryParseWarning: '',
      salaryEditor: emptySalaryStructureData(),
      activeSalaryStructureIndex: 0,
      syncingSalary: false,
      templateEditor: {
        mode: 'empty',
        fields: emptyTemplateFields(),
        regions: null,
      },
      syncingFromHtml: false,
      templateEditorReady: false,
      editorMountKey: 0,
      // Quill options are built per-editor in OfferConfigureStepLetter.vue — do not call buildOfferQuillOptions() here.
      tableFields: TABLE_FIELDS,
    }
  },
  computed: {
    canView() {
      return canViewOfferConfigure(this.$can.bind(this))
    },
    canManage() {
      return canManageOfferConfigure(this.$can.bind(this))
    },
    formModalTitle() {
      if (this.isReplicating) return 'Replicate Offer Template'
      if (this.isEditing) return this.canManage ? 'Edit Offer Template' : 'View Offer Template'
      return 'New Offer Template'
    },
    editBannerSite() {
      return this.form.site_name || this.form.site_code || 'this site'
    },
    editBannerClient() {
      return this.form.client || '—'
    },
    /** Site code locked on edit (and when viewing). Replicate stays editable for the new key. */
    isSiteCodeDisabled() {
      return !this.canManage || this.isEditing
    },
    /**
     * Site name locked on edit, or on create once autofilled from site code + client.
     * Replicate stays editable so the copy can be retargeted.
     */
    isSiteNameDisabled() {
      if (!this.canManage) return true
      if (this.isEditing) return true
      if (this.isReplicating) return false
      return !!(this.form.site_code.trim() && this.form.client.trim() && this.form.site_name.trim())
    },
    /** Client locked on edit when already set from the existing config. */
    isClientDisabled() {
      if (!this.canManage) return true
      if (this.isEditing && String(this.form.client || '').trim()) return true
      return false
    },
    /** Location locked on edit so site identity stays aligned with Client Dashboard. */
    isLocationDisabled() {
      if (!this.canManage) return true
      return this.isEditing
    },
    isFormValid() {
      return !!(this.form.site_code && this.form.site_name && this.form.jobTitle)
    },
    filteredConfigs() {
      const q = String(this.searchQuery || '').trim().toLowerCase()
      const list = Array.isArray(this.configs) ? this.configs : []
      // Toggle OFF = Active only; ON = Inactive only (never mix).
      const byStatus = list.filter(c => (this.showInactive ? !c.is_active : !!c.is_active))
      if (!q) return byStatus
      return byStatus.filter(c => {
        if (!c || typeof c !== 'object') return false
        const haystack = [
          c.site_code,
          c.site_name,
          c.client,
          c.jobTitle,
          c.location,
          c.project_type,
        ].map(v => String(v || '').toLowerCase())
        return haystack.some(v => v.includes(q))
      })
    },
    activeConfigCount() {
      return this.filteredConfigs.filter(c => c.is_active).length
    },
    inactiveConfigCount() {
      return this.filteredConfigs.filter(c => !c.is_active).length
    },
    uploadedImageCount() {
      return this.imageKeys.filter(k => !!this.form.images[k]).length
    },
    /** Remount iframe once when the first real srcdoc arrives (empty → letter). */
    livePreviewMountKey() {
      return this.livePreviewSrcdoc === this.emptyPreviewSrcdoc ? 'empty' : 'ready'
    },
    hasPreviewContent() {
      const fields = (this.templateEditor && this.templateEditor.fields) || {}
      const sections = normalizeOfferSections(fields.offerSections)
      const anyIncluded = OFFER_SECTION_KEYS.some(key => sections[key] !== false)
      return !!(
        (this.form && this.form.htmlTemplate)
        || fields.body
        || fields.intro
        || fields.jobResponsibilities
        || fields.termsAndConditions
        || fields.salaryStructure
        || anyIncluded
      )
    },
    letterSectionChips() {
      const fields = (this.templateEditor && this.templateEditor.fields) || {}
      const sections = normalizeOfferSections(fields.offerSections)
      return LETTER_SECTION_CHIP_DEFS.map(def => ({
        ...def,
        included: sections[def.key] !== false,
      }))
    },
    includedLetterSectionsLabel() {
      const shown = this.letterSectionChips.filter(chip => chip.included)
      if (!shown.length) return 'None'
      if (shown.length === this.letterSectionChips.length) return 'All'
      return shown.map(chip => chip.label).join(', ')
    },
    excludedLetterSectionsLabel() {
      const hidden = this.letterSectionChips.filter(chip => !chip.included)
      if (!hidden.length) return ''
      return hidden.map(chip => chip.label).join(', ')
    },
    layoutDefaults() {
      const fields = (this.templateEditor && this.templateEditor.fields) || {}
      return normalizeOfferLayout(fields.offerLayout).defaults
    },
    hasListFilters() {
      return !!String(this.searchQuery || '').trim() || this.showInactive
    },
    canWizardNext() {
      if (this.wizardStep === 0) return this.isFormValid
      return true
    },
    wizardCompletionPercent() {
      const totalSteps = this.wizardStepLabels.length
      if (!totalSteps) return 0
      return Math.round(((this.wizardStep + 1) / totalSteps) * 100)
    },
    offerSignatureLocationsModel: {
      get() {
        const fields = this.templateEditor.fields || {}
        return normalizeOfferSignatureLocations(
          fields.offerSignaturePages != null
            ? fields.offerSignaturePages
            : fields.offerSignaturePlacement,
        )
      },
      set(value) {
        this.onSignaturePlacementChange(value)
      },
    },
    employeeSignPlacement() {
      const fields = this.templateEditor.fields || {}
      return normalizeOfferEmployeeSignConfig(
        fields.offerEmployeeSignConfig != null
          ? fields.offerEmployeeSignConfig
          : fields.offerEmployeeSignPlacement,
      )
    },
    letterTitleText: {
      get() {
        return this.sectionLayoutResolved('title').titleText || 'OFFER LETTER'
      },
      set(value) {
        this.onSectionLayoutChange('title', 'titleText', String(value || '').trim() || 'OFFER LETTER')
      },
    },
    signatureSummaryLabel() {
      const locs = this.offerSignatureLocationsModel || []
      if (!locs.length) return 'None'
      const labels = OFFER_SIGNATURE_PLACEMENT_OPTIONS
        .filter(opt => locs.includes(opt.value))
        .map(opt => opt.text.replace(/\s*\(.*\)\s*$/, '').trim())
      return labels.length ? labels.join('; ') : locs.join(', ')
    },
    letterSectionPickerItems() {
      const fields = (this.templateEditor && this.templateEditor.fields) || {}
      const sections = normalizeOfferSections(fields.offerSections)
      return LETTER_SECTION_EDITOR_DEFS.map(def => ({
        key: def.sectionKey,
        number: def.number,
        label: def.label,
        hint: def.hint,
        included: sections[def.sectionKey] !== false,
        isSalaryStep: !!def.isSalaryStep,
      }))
    },
    salarySectionIncluded() {
      const fields = (this.templateEditor && this.templateEditor.fields) || {}
      const sections = normalizeOfferSections(fields.offerSections)
      return sections.salary !== false
    },
    salaryComponentRowCount() {
      return countSalaryStructureComponentRows(this.salaryEditor)
    },
    includedOrderedLetterSections() {
      const fields = (this.templateEditor && this.templateEditor.fields) || {}
      const sections = normalizeOfferSections(fields.offerSections)
      const order = normalizeOfferSectionOrder(fields.offerSectionOrder)
      return order
        .filter(key => LETTER_CONTENT_SECTION_KEYS.includes(key))
        .filter(key => sections[key] !== false)
        .map(key => LETTER_SECTION_EDITOR_DEFS.find(def => def.sectionKey === key))
        .filter(Boolean)
    },
    fontSizeSelectOptions() {
      return OFFER_FONT_SIZE_OPTIONS.map(size => ({
        value: size,
        text: `${size} px`,
      }))
    },
  },
  watch: {
    'form.site_code'() {
      this.syncAutoSiteName()
    },
    'form.client'() {
      this.syncAutoSiteName()
    },
    'form.jobTitle'() {
      this.scheduleLivePreviewRefresh()
    },
    'form.location'() {
      this.scheduleLivePreviewRefresh()
    },
    'form.htmlTemplate'() {
      this.scheduleLivePreviewRefresh()
    },
    'form.images': {
      deep: true,
      handler() {
        this.scheduleLivePreviewRefresh()
      },
    },
    'templateEditor.fields': {
      deep: true,
      handler() {
        this.scheduleLivePreviewRefresh()
      },
    },
    'templateEditor.mode'() {
      this.scheduleLivePreviewRefresh()
    },
    'salaryEditor': {
      deep: true,
      handler() {
        this.scheduleLivePreviewRefresh()
      },
    },
    showFormModal(open) {
      if (open) {
        this.scheduleLivePreviewRefresh(true)
      } else {
        this.clearLivePreview()
        this.showMobilePreview = false
      }
    },
    formLoading(loading) {
      if (!loading && this.showFormModal) {
        this.scheduleLivePreviewRefresh(true)
      }
    },
    wizardStep(step) {
      if (step === 3) {
        this.syncBeforePreviewStep()
      }
      if (step > this.maxWizardStepReached) {
        this.maxWizardStepReached = step
      }
      if (step === 1 && !this.activeLetterSection) {
        this.expandDefaultLetterSection()
      }
      // Write Letter must paint immediately; other steps can debounce.
      this.scheduleLivePreviewRefresh(step === 1)
    },
    templateEditorReady(ready) {
      if (ready && this.showFormModal) {
        this.syncTemplateFromFields()
        this.scheduleLivePreviewRefresh(true)
      }
    },
    includedOrderedLetterSections(sections) {
      if (!sections.length) {
        this.activeLetterSection = ''
        return
      }
      if (
        this.activeLetterSection
        && !sections.some(s => s.sectionKey === this.activeLetterSection)
      ) {
        this.activeLetterSection = ''
      }
    },
  },
  created() {
    if (this.canView) {
      this.loadConfigs()
    }
  },
  beforeDestroy() {
    clearTimeout(this.previewRefreshTimer)
  },
  methods: {
    toast(title, variant = 'success') {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon: variant === 'success' ? 'CheckIcon' : 'AlertCircleIcon', variant },
      }, { timeout: 3000 })
    },

    onShowInactiveChange(value) {
      this.showInactive = value
      this.loadConfigs()
    },

    clearListFilters() {
      this.searchQuery = ''
    },

    canGoToWizardStep(idx) {
      if (idx <= this.wizardStep) return true
      if (idx <= this.maxWizardStepReached) return true
      if (idx === this.wizardStep + 1 && this.canWizardNext) return true
      return false
    },

    goWizardStep(idx) {
      if (!this.canGoToWizardStep(idx)) return
      if (idx > this.wizardStep) {
        if (!this.validateWizardStep(this.wizardStep)) return
        this.syncOnStepLeave(this.wizardStep)
      }
      this.wizardStep = idx
    },

    goWizardNext() {
      if (!this.validateWizardStep(this.wizardStep)) return
      this.syncOnStepLeave(this.wizardStep)
      if (this.wizardStep < this.wizardStepLabels.length - 1) {
        this.wizardStep += 1
      }
    },

    goWizardPrev() {
      if (this.wizardStep > 0) {
        this.wizardStep -= 1
      }
    },

    validateWizardStep(step) {
      if (step === 0 && !this.isFormValid) {
        this.toast('Site code, site name, and job title are required.', 'warning')
        return false
      }
      return true
    },

    syncOnStepLeave(step) {
      if (step === 2 && this.templateEditor.mode !== 'raw') {
        this.applySalaryEditorToFields()
      }
      if (step >= 1 && this.templateEditor.mode !== 'raw') {
        this.syncTemplateFromFields()
      }
    },

    syncBeforePreviewStep() {
      if (this.templateEditor.mode !== 'raw') {
        this.applySalaryEditorToFields()
      }
      this.syncTemplateFromFields()
    },

    normalizeConfig(row) {
      const configuration = parseJson(row.configuration, {})
      const metadata = parseJson(row.metadata, {})
      return {
        ...row,
        jobTitle: configuration.jobTitle || '',
        htmlTemplate: extractOfferHtmlFromMetadata(metadata),
        images: extractOfferImagesFromMetadata(metadata),
        offerSections: normalizeOfferSections(metadata.offerSections),
        offerSectionDrafts: metadata.offerSectionDrafts && typeof metadata.offerSectionDrafts === 'object'
          ? metadata.offerSectionDrafts
          : {},
        offerSectionOrder: normalizeOfferSectionOrder(metadata.offerSectionOrder),
        offerSalaryComponentOrder: Array.isArray(metadata.offerSalaryComponentOrder)
          ? metadata.offerSalaryComponentOrder.filter(v => typeof v === 'string')
          : [],
        offerLayout: normalizeOfferLayout(metadata.offerLayout),
        offerSignaturePlacement: resolveOfferSignaturePagesFromMetadata(metadata),
        offerSignaturePages: resolveOfferSignaturePagesFromMetadata(metadata),
        offerEmployeeSignPlacement: resolveOfferEmployeeSignConfigFromMetadata(metadata),
        offerEmployeeSignConfig: resolveOfferEmployeeSignConfigFromMetadata(metadata),
      }
    },

    async loadConfigs() {
      this.loading = true
      this.loadError = ''
      try {
        const wantActive = !this.showInactive
        const res = await siteConfigService.getAllOfferConfigs({ is_active: wantActive })
        if (res.status !== 'OK') {
          this.loadError = res.message || 'Failed to load configurations'
          this.configs = []
          return
        }
        const rows = Array.isArray(res.data) ? res.data : []
        this.configs = rows.map(row => {
          const normalized = this.normalizeConfig(row)
          return {
            ...normalized,
            jobTitle: normalized.jobTitle,
          }
        }).filter(c => (wantActive ? !!c.is_active : !c.is_active))
      } catch (e) {
        this.loadError = e.message || 'Failed to load configurations'
        this.configs = []
      } finally {
        this.loading = false
      }
    },

    openCreateForm() {
      this.isEditing = false
      this.isReplicating = false
      this.editingId = null
      this.existingMetadata = null
      this.siteNameManuallyEdited = false
      this.wizardStep = 0
      this.maxWizardStepReached = 0
      this.templateEditorReady = false
      this.form = emptyForm()
      this.initTemplateEditor('')
      this.editorMountKey += 1
      this.showFormModal = true
      this.$nextTick(() => {
        this.templateEditorReady = true
      })
    },

    getCurrentUserEmpId() {
      try {
        const userData = JSON.parse(localStorage.getItem('userData') || '{}')
        return userData.emp_id || userData.id || null
      } catch (e) {
        return null
      }
    },

    async openEditForm(item) {
      const listId = item && item.id != null ? item.id : null
      if (listId == null) {
        this.toast('Cannot edit: this row has no id from the server.', 'danger')
        return
      }
      if (this.formLoading) return

      this.isEditing = true
      this.isReplicating = false
      this.editingId = listId
      this.formLoading = true
      this.showFormModal = true
      try {
        const res = await siteConfigService.getSiteById(listId)
        if (res.status !== 'OK' || !res.data) {
          this.isEditing = false
          this.editingId = null
          this.showFormModal = false
          this.toast(res.message || 'Failed to load config details', 'danger')
          return
        }
        this.populateFormFromRecord(res.data, { idFallback: listId })
        // Jump to letter content — identity is locked; content is the main edit job.
        this.wizardStep = 1
        this.expandDefaultLetterSection()
        // Assemble + srcdoc BEFORE the loading spinner unmounts so the iframe
        // first-paints with letter HTML (section toggle used to be the first sync).
        this.hydrateAndPrimeLivePreview()
      } catch (e) {
        this.isEditing = false
        this.editingId = null
        this.showFormModal = false
        this.toast(e.message || 'Failed to load config details', 'danger')
      } finally {
        this.formLoading = false
      }
    },

    async replicateConfig(item) {
      if (this.formLoading) return
      this.isEditing = false
      this.isReplicating = true
      this.editingId = null
      this.formLoading = true
      this.showFormModal = true
      try {
        const res = await siteConfigService.getSiteById(item.id)
        if (res.status !== 'OK' || !res.data) {
          this.isReplicating = false
          this.showFormModal = false
          this.toast(res.message || 'Failed to load config to replicate', 'danger')
          return
        }
        this.populateFormFromRecord(res.data, { replicate: true })
        this.hydrateAndPrimeLivePreview()
      } catch (e) {
        this.isReplicating = false
        this.showFormModal = false
        this.toast(e.message || 'Failed to load config to replicate', 'danger')
      } finally {
        this.formLoading = false
      }
    },

    populateFormFromRecord(record, { replicate = false, idFallback = null } = {}) {
      this.templateEditorReady = false
      this.activeLetterSection = ''
      this.showLetterSettingsModal = false
      const normalized = this.normalizeConfig(record)
      const suffix = replicate ? '_copy' : ''
      const siteCode = replicate ? `${normalized.site_code}${suffix}` : normalized.site_code
      const client = normalized.client || ''
      const recordId = record && record.id != null ? record.id : idFallback
      this.existingMetadata = parseJson(record && record.metadata, {})
      this.siteNameManuallyEdited = !replicate
      this.wizardStep = 0
      this.maxWizardStepReached = WIZARD_STEP_LABELS.length - 1
      if (!replicate && recordId != null) {
        this.editingId = recordId
      }
      this.form = {
        id: replicate ? null : recordId,
        site_code: siteCode,
        site_name: replicate
          ? buildAutoSiteName(siteCode, client)
          : normalized.site_name,
        client,
        project_type: normalized.project_type || '',
        location: normalized.location || '',
        jobTitle: normalized.jobTitle || '',
        is_active: replicate ? true : normalized.is_active !== false,
        htmlTemplate: normalized.htmlTemplate || '',
        images: withDefaultOfferBranding(normalized.images),
      }
      this.initTemplateEditor(this.form.htmlTemplate, {
        offerSections: normalized.offerSections,
        offerSectionDrafts: normalized.offerSectionDrafts,
        offerSectionOrder: normalized.offerSectionOrder,
        offerSalaryComponentOrder: normalized.offerSalaryComponentOrder,
        offerLayout: normalized.offerLayout,
        offerSignaturePlacement: normalized.offerSignaturePages || normalized.offerSignaturePlacement,
        offerEmployeeSignPlacement: normalized.offerEmployeeSignConfig
          || normalized.offerEmployeeSignPlacement,
      })
      this.editorMountKey += 1
    },

    syncAutoSiteName() {
      if (this.siteNameManuallyEdited) return
      const next = buildAutoSiteName(this.form.site_code, this.form.client)
      if (this.form.site_name === next) return
      this._syncingSiteName = true
      this.form.site_name = next
      this.$nextTick(() => {
        this._syncingSiteName = false
      })
    },

    onSiteNameEdited(val) {
      if (this._syncingSiteName) return
      this.siteNameManuallyEdited = !!(val && String(val).trim())
    },

    initTemplateEditor(html, {
      offerSections,
      offerSectionDrafts,
      offerSectionOrder,
      offerSalaryComponentOrder,
      offerLayout,
      offerSignaturePlacement,
      offerEmployeeSignPlacement,
    } = {}) {
      const sections = normalizeOfferSections(offerSections)
      const sectionOrder = normalizeOfferSectionOrder(offerSectionOrder)
      const placement = normalizeOfferSignatureLocations(offerSignaturePlacement)
      const employeeSign = normalizeOfferEmployeeSignConfig(offerEmployeeSignPlacement)
      const layout = applyOfferSignaturePlacementToLayout(
        normalizeOfferLayout(offerLayout),
        placement,
      )
      const salaryComponentOrder = Array.isArray(offerSalaryComponentOrder)
        ? offerSalaryComponentOrder.filter(v => typeof v === 'string')
        : []
      if (!html || !html.trim()) {
        this.templateEditor = {
          mode: 'empty',
          fields: fillStaleSectionDrafts(
            applyOfferSectionDrafts(
              {
                ...emptyTemplateFields(),
                offerSections: sections,
                offerSectionOrder: sectionOrder,
                offerLayout: layout,
                offerSignaturePlacement: placement,
                offerSignaturePages: placement,
                offerEmployeeSignPlacement: employeeSign,
                offerEmployeeSignConfig: employeeSign,
              },
              offerSectionDrafts,
            ),
            offerSectionDrafts,
            '',
          ),
          regions: null,
        }
        this.form.htmlTemplate = ''
        this.initSalaryEditor('', salaryComponentOrder)
        return
      }
      const parsed = parseOfferTemplate(html)
      // Hidden sections ← drafts; ON sections also restore when HTML body is
      // empty/stale/polluted (replicate: Policies ON but markers emptied).
      const fields = fillStaleSectionDrafts(
        applyOfferSectionDrafts(
          {
            ...parsed.fields,
            offerSections: sections,
            offerSectionOrder: sectionOrder,
            offerLayout: layout,
            offerSignaturePlacement: placement,
            offerSignaturePages: placement,
            offerEmployeeSignPlacement: employeeSign,
            offerEmployeeSignConfig: employeeSign,
          },
          offerSectionDrafts,
        ),
        offerSectionDrafts,
        html,
      )
      if (fields.includePageBreak === false) {
        fields.offerLayout.sections.salary.pageBreakBefore = false
      }
      this.templateEditor = {
        mode: parsed.mode,
        fields,
        regions: parsed.regions,
      }
      if (parsed.mode === 'empty') {
        this.form.htmlTemplate = ''
      }
      this.initSalaryEditor(this.templateEditor.fields.salaryStructure, salaryComponentOrder)
    },

    ensureOfferSectionOrder() {
      if (!this.templateEditor.fields.offerSectionOrder) {
        this.$set(
          this.templateEditor.fields,
          'offerSectionOrder',
          defaultOfferSectionOrder(),
        )
      }
      return this.templateEditor.fields.offerSectionOrder
    },

    onSectionVisibilityChange() {
      if (!this.templateEditor.fields.offerSections) {
        this.$set(this.templateEditor.fields, 'offerSections', defaultOfferSections())
      }
      this.syncTemplateFromFields()
    },

    ensureOfferLayout() {
      if (!this.templateEditor.fields.offerLayout) {
        this.$set(this.templateEditor.fields, 'offerLayout', normalizeOfferLayout(null))
      }
      return this.templateEditor.fields.offerLayout
    },

    sectionLayoutResolved(sectionKey) {
      return resolveSectionLayout(this.ensureOfferLayout(), sectionKey)
    },

    onLayoutDefaultChange(prop, value) {
      const layout = this.ensureOfferLayout()
      if (!layout.defaults) this.$set(layout, 'defaults', defaultOfferLayout().defaults)
      this.$set(layout.defaults, prop, value)
      this.onOfferLayoutChange()
    },

    onSectionLayoutChange(sectionKey, prop, value) {
      const layout = this.ensureOfferLayout()
      if (!layout.sections) this.$set(layout, 'sections', defaultOfferLayout().sections)
      if (!layout.sections[sectionKey]) {
        this.$set(layout.sections, sectionKey, {})
      }
      this.$set(layout.sections[sectionKey], prop, value)
      if (sectionKey === 'salary' && prop === 'pageBreakBefore') {
        this.$set(this.templateEditor.fields, 'includePageBreak', value !== false)
      }
      if ((sectionKey === 'closing' || sectionKey === 'signature') && prop === 'pageBreakBefore') {
        const fields = this.templateEditor.fields || {}
        const currentLocs = normalizeOfferSignatureLocations(
          fields.offerSignaturePages != null
            ? fields.offerSignaturePages
            : fields.offerSignaturePlacement,
        )
        // Multi-select (e.g. withClosing + lastPage) is owned by the signature
        // checkboxes — page-break toggles must not collapse it to lastPage-only.
        if (currentLocs.length <= 1) {
          const closingBreak = sectionKey === 'closing'
            ? value === true
            : this.sectionLayoutResolved('closing').pageBreakBefore === true
          const signatureBreak = sectionKey === 'signature'
            ? value === true
            : this.sectionLayoutResolved('signature').pageBreakBefore === true
          let placement = [...DEFAULT_OFFER_SIGNATURE_LOCATIONS]
          if (!closingBreak && signatureBreak) placement = [OFFER_SIGNATURE_PLACEMENT.AFTER_TERMS]
          else if (!closingBreak && !signatureBreak) placement = [OFFER_SIGNATURE_PLACEMENT.WITH_CLOSING]
          this.$set(this.templateEditor.fields, 'offerSignaturePlacement', placement)
          this.$set(this.templateEditor.fields, 'offerSignaturePages', placement)
        }
      }
      this.onOfferLayoutChange()
    },

    sectionPageStartMode(sectionKey) {
      return derivePageStartMode(this.sectionLayoutResolved(sectionKey), sectionKey)
    },

    onPageStartModeChange(sectionKey, mode) {
      if (!this.canManage || this.templateEditor.mode === 'raw') return
      const layout = this.ensureOfferLayout()
      if (!layout.sections) this.$set(layout, 'sections', defaultOfferLayout().sections)
      const current = layout.sections[sectionKey] || {}
      const next = applyPageStartModeToSection(current, mode)
      this.$set(layout.sections, sectionKey, next)
      if (sectionKey === 'salary') {
        this.$set(this.templateEditor.fields, 'includePageBreak', mode !== OFFER_PAGE_START_MODE.CONTINUE)
      }
      this.onOfferLayoutChange()
    },

    onSectionTitleTextChange(sectionKey, value) {
      if (!this.canManage || this.templateEditor.mode === 'raw') return
      const text = String(value || '').trim()
      this.onSectionLayoutChange(sectionKey, 'sectionTitleText', text)
      if (sectionKey === 'salary' && text) {
        this.salaryEditor = normalizeSalaryStructureData({
          ...this.salaryEditor,
          heading: text,
        })
        this.onSalaryEditorChange()
      }
    },

    onSectionTitleAlignChange(sectionKey, value) {
      if (!this.canManage || this.templateEditor.mode === 'raw') return
      this.onSectionLayoutChange(sectionKey, 'sectionTitleAlign', value)
    },

    onSectionTitleFontSizeChange(sectionKey, value) {
      if (!this.canManage || this.templateEditor.mode === 'raw') return
      this.onSectionLayoutChange(sectionKey, 'sectionTitleFontSizePx', value)
    },

    onSectionTitleFontWeightChange(sectionKey, value) {
      if (!this.canManage || this.templateEditor.mode === 'raw') return
      this.onSectionLayoutChange(sectionKey, 'sectionTitleFontWeight', value)
    },

    onLetterTitleAlignChange(value) {
      this.onSectionLayoutChange('title', 'titleAlign', value)
    },

    letterAccordionId(sectionKey) {
      return `oc-letter-acc-${sectionKey}`
    },

    onLetterAccordionHeaderClick(sectionKey) {
      if (this.activeLetterSection === sectionKey) {
        this.activeLetterSection = ''
        return
      }
      this.openLetterSection(sectionKey)
    },

    openLetterSection(sectionKey) {
      if (sectionKey === 'salary') {
        if (!this.salarySectionIncluded) return
        this.wizardStep = 2
        return
      }
      const included = this.includedOrderedLetterSections.some(
        s => s.sectionKey === sectionKey,
      )
      if (!included) return
      this.activeLetterSection = sectionKey
      this.$nextTick(() => {
        const el = document.getElementById(`oc-section-${sectionKey}`)
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    },

    moveLetterSection(sectionKey, direction) {
      if (!this.canManage || this.templateEditor.mode === 'raw') return
      const visibleKeys = this.includedOrderedLetterSections.map(s => s.sectionKey)
      const idx = visibleKeys.indexOf(sectionKey)
      if (idx < 0) return
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1
      if (swapIdx < 0 || swapIdx >= visibleKeys.length) return
      const otherKey = visibleKeys[swapIdx]
      const order = [...this.ensureOfferSectionOrder()]
      const i = order.indexOf(sectionKey)
      const j = order.indexOf(otherKey)
      if (i < 0 || j < 0) return
      order[i] = otherKey
      order[j] = sectionKey
      this.$set(this.templateEditor.fields, 'offerSectionOrder', order)
      this.syncTemplateFromFields()
    },

    toggleLetterSection(sectionKey) {
      if (!this.canManage || this.templateEditor.mode === 'raw') return
      if (!this.templateEditor.fields.offerSections) {
        this.$set(this.templateEditor.fields, 'offerSections', defaultOfferSections())
      }
      const current = this.templateEditor.fields.offerSections[sectionKey] !== false
      this.$set(this.templateEditor.fields.offerSections, sectionKey, !current)
      this.onSectionVisibilityChange()
      if (current && this.activeLetterSection === sectionKey) {
        this.$nextTick(() => {
          const remaining = this.includedOrderedLetterSections
          this.activeLetterSection = remaining[0] ? remaining[0].sectionKey : ''
        })
      }
      if (!current) {
        this.openLetterSection(sectionKey)
      }
    },

    onSalaryAnnexureInput(data) {
      this.salaryEditor = normalizeSalaryStructureData(data)
      const structures = this.salaryEditor.structures || []
      if (this.activeSalaryStructureIndex >= structures.length) {
        this.activeSalaryStructureIndex = Math.max(0, structures.length - 1)
      }
      const heading = String(this.salaryEditor.heading || '').trim()
      if (heading) {
        this.onSectionLayoutChange('salary', 'sectionTitleText', heading)
      }
      this.onSalaryEditorChange()
    },

    showAllLetterSections() {
      if (!this.canManage || this.templateEditor.mode === 'raw') return
      const next = defaultOfferSections()
      this.$set(this.templateEditor.fields, 'offerSections', next)
      this.onSectionVisibilityChange()
      this.$nextTick(() => {
        const first = this.includedOrderedLetterSections[0]
        if (first) this.openLetterSection(first.sectionKey)
      })
    },

    onSignaturePlacementChange(value) {
      if (!this.canManage || this.templateEditor.mode === 'raw') return
      let locs = normalizeOfferSignatureLocations(value)
      // Keep at least one location selected.
      if (!locs.length) locs = [...DEFAULT_OFFER_SIGNATURE_LOCATIONS]
      this.$set(this.templateEditor.fields, 'offerSignaturePlacement', locs)
      this.$set(this.templateEditor.fields, 'offerSignaturePages', locs)
      const nextLayout = applyOfferSignaturePlacementToLayout(this.ensureOfferLayout(), locs)
      this.$set(this.templateEditor.fields, 'offerLayout', nextLayout)
      this.syncTemplateFromFields()
      this.scheduleLivePreviewRefresh(true)
    },

    onEmployeeSignPlacementChange(prop, value) {
      if (!this.canManage || this.templateEditor.mode === 'raw') return
      const fields = this.templateEditor.fields || {}
      const current = normalizeOfferEmployeeSignConfig(
        fields.offerEmployeeSignConfig != null
          ? fields.offerEmployeeSignConfig
          : fields.offerEmployeeSignPlacement,
      )
      // Coerce checkbox payloads (BV boolean, or native event) so unchecks stick.
      let nextValue = value
      if (prop !== 'layout') {
        if (typeof value === 'boolean') nextValue = value
        else if (value && typeof value === 'object' && value.target) {
          nextValue = !!value.target.checked
        } else {
          nextValue = value === true || value === 'true' || value === 1 || value === '1'
        }
      }
      const next = normalizeOfferEmployeeSignConfig({ ...current, [prop]: nextValue })
      this.$set(this.templateEditor.fields, 'offerEmployeeSignPlacement', next)
      this.$set(this.templateEditor.fields, 'offerEmployeeSignConfig', next)
      this.syncTemplateFromFields()
      this.scheduleLivePreviewRefresh(true)
    },

    buildChromeOptionsFromFields() {
      const fields = this.templateEditor.fields || {}
      // Do NOT pass form.images.signature here — that would embed base64 into the
      // saved HTML template and roughly double payload size (images already live in
      // metadata.images). Live preview fills {{METADATA.IMAGES.SIGNATURE}} via
      // buildPreviewHtml(form.images); generate inlines from metadata/HTML.
      return {
        offerSignaturePlacement: resolveFieldsSignaturePages(fields),
        offerEmployeeSignPlacement: resolveFieldsEmployeeSignConfig(fields),
        signatoryName: fields.signatoryName || '',
        signatoryDesignation: fields.signatoryDesignation || '',
      }
    },

    onOfferLayoutChange() {
      this.syncTemplateFromFields()
    },

    initSalaryEditor(html, componentOrder = null) {
      const result = parseSalaryTableHtml(html)
      this.salaryParseWarning = result.warning || ''
      let structures = (result.data.structures || []).map(block => {
        let rows = syncSalaryRowsYearly((block.rows || []).map(row => ({ ...row })))
        if (Array.isArray(componentOrder) && componentOrder.length) {
          rows = reorderSalaryRowsByComponentOrder(rows, componentOrder)
        }
        return {
          title: block.title || '',
          columnLabels: block.columnLabels,
          rows,
        }
      })
      if (!structures.length) {
        structures = normalizeSalaryStructureData(result.data).structures
      }
      this.salaryEditor = normalizeSalaryStructureData({
        heading: result.data.heading,
        structures,
        notes: result.data.notes,
        columnLabels: structures[0] && structures[0].columnLabels,
        rows: structures[0] && structures[0].rows,
      })
      this.activeSalaryStructureIndex = 0
      if (!result.parsed && html && html.trim()) {
        this.salaryParseWarning = result.warning || 'Could not parse existing salary table — review rows below.'
      }
      if (result.parsed && (!html || !html.trim())) {
        this.applySalaryEditorToFields()
      } else if (Array.isArray(componentOrder) && componentOrder.length && result.parsed) {
        // Persist reordered table HTML when metadata order differs from markup order.
        this.applySalaryEditorToFields()
      }
    },

    applySalaryEditorToFields() {
      this.templateEditor.fields.salaryStructure = buildSalaryTableHtml(this.salaryEditor)
    },

    onSalaryEditorChange() {
      if (this.syncingSalary || this.templateEditor.mode === 'raw') return
      this.salaryParseWarning = ''
      this.applySalaryEditorToFields()
      this.syncTemplateFromFields()
    },

    ensureSalaryStructures() {
      const normalized = normalizeSalaryStructureData(this.salaryEditor)
      this.salaryEditor = normalized
      return normalized.structures
    },

    resolveStructureIndex(structureIndex) {
      const structures = this.ensureSalaryStructures()
      const idx = structureIndex == null ? this.activeSalaryStructureIndex : structureIndex
      if (idx < 0 || idx >= structures.length) return 0
      return idx
    },

    moveSalaryRow(structureIndex, index, direction) {
      if (!this.canManage || this.templateEditor.mode === 'raw') return
      // Backward-compatible: older emitters passed (index, direction) only.
      let structIdx = structureIndex
      let rowIdx = index
      let dir = direction
      if (direction == null && (index === 'up' || index === 'down')) {
        dir = index
        rowIdx = structureIndex
        structIdx = this.activeSalaryStructureIndex
      }
      const sIdx = this.resolveStructureIndex(structIdx)
      const structures = this.ensureSalaryStructures().map((block, i) => {
        if (i !== sIdx) return block
        return {
          ...block,
          rows: moveSalaryRowOrder(block.rows, rowIdx, dir),
        }
      })
      this.salaryEditor = normalizeSalaryStructureData({ ...this.salaryEditor, structures })
      this.onSalaryEditorChange()
    },

    addSalaryRow(structureIndex) {
      if (!this.canManage || this.templateEditor.mode === 'raw') return
      const sIdx = this.resolveStructureIndex(structureIndex)
      const structures = this.ensureSalaryStructures().map((block, i) => {
        if (i !== sIdx) return block
        return {
          ...block,
          rows: [
            ...(block.rows || []),
            {
              component: '',
              monthly: '',
              yearly: '',
              rowType: 'normal',
            },
          ],
        }
      })
      this.salaryEditor = normalizeSalaryStructureData({ ...this.salaryEditor, structures })
      this.onSalaryEditorChange()
    },

    removeSalaryRow(structureIndex, rowIndex) {
      if (!this.canManage || this.templateEditor.mode === 'raw') return
      // Backward-compatible: older emitters passed rowIndex only.
      let sIdx = structureIndex
      let rIdx = rowIndex
      if (rowIndex == null) {
        rIdx = structureIndex
        sIdx = this.activeSalaryStructureIndex
      }
      sIdx = this.resolveStructureIndex(sIdx)
      const structures = this.ensureSalaryStructures().map((block, i) => {
        if (i !== sIdx) return block
        if (!Array.isArray(block.rows) || block.rows.length <= 1) return block
        const rows = block.rows.slice()
        rows.splice(rIdx, 1)
        return { ...block, rows }
      })
      this.salaryEditor = normalizeSalaryStructureData({ ...this.salaryEditor, structures })
      this.onSalaryEditorChange()
    },

    addSalaryStructure() {
      if (!this.canManage || this.templateEditor.mode === 'raw') return
      const normalized = normalizeSalaryStructureData(this.salaryEditor)
      const structures = (normalized.structures || []).map(block => ({ ...block }))
      // When going multi-structure, ensure the first table has a visible title.
      if (structures.length === 1 && !String(structures[0].title || '').trim()) {
        structures[0] = {
          ...structures[0],
          title: defaultSalaryStructureTitle(0),
        }
      }
      const cloneFrom = structures[structures.length - 1] || emptySalaryStructureBlock()
      structures.push({
        ...emptySalaryStructureBlock(defaultSalaryStructureTitle(structures.length)),
        columnLabels: { ...(cloneFrom.columnLabels || {}) },
        rows: (cloneFrom.rows || []).map(row => ({
          ...row,
          monthly: '',
          yearly: '',
        })),
      })
      this.salaryEditor = normalizeSalaryStructureData({ ...normalized, structures })
      this.activeSalaryStructureIndex = structures.length - 1
      this.onSalaryEditorChange()
    },

    removeSalaryStructure(structureIndex) {
      if (!this.canManage || this.templateEditor.mode === 'raw') return
      const normalized = normalizeSalaryStructureData(this.salaryEditor)
      const structures = (normalized.structures || []).slice()
      if (structures.length <= 1) return
      const idx = this.resolveStructureIndex(structureIndex)
      structures.splice(idx, 1)
      // Collapse back to single untitled structure for legacy heading-only HTML.
      if (structures.length === 1) {
        structures[0] = { ...structures[0], title: '' }
      }
      this.salaryEditor = normalizeSalaryStructureData({ ...normalized, structures })
      this.activeSalaryStructureIndex = Math.min(idx, structures.length - 1)
      this.onSalaryEditorChange()
    },

    onLetterFieldInput(fieldKey, html) {
      if (!this.templateEditor.fields) return
      this.$set(this.templateEditor.fields, fieldKey, html == null ? '' : html)
      this.syncTemplateFromFields()
    },

    syncTemplateFromFields() {
      if (this.syncingFromHtml || this.templateEditor.mode === 'raw' || !this.templateEditorReady) return
      const fields = this.templateEditor.fields || {}
      const baseHtml = this.form.htmlTemplate || buildDefaultShell(fields)
      this.form.htmlTemplate = assembleOfferTemplate(
        baseHtml,
        fields,
        this.templateEditor,
      )
      const bodySnippet = plainTextSnippet(fields.body)
      if (
        isSectionVisible(fields, 'body')
        && bodySnippet.length >= 12
        && !htmlContainsPlainSnippet(this.form.htmlTemplate, bodySnippet)
      ) {
        this.form.htmlTemplate = repairOfferBodyAfterChrome(this.form.htmlTemplate, fields)
      }
      // After first successful assemble, markers exist — keep mode/regions aligned
      // so later saves use marker replacement (do not overwrite Quill field values).
      if (this.form.htmlTemplate && this.templateEditor.mode === 'empty') {
        const parsed = parseOfferTemplate(this.form.htmlTemplate)
        if (parsed.mode === 'structured') {
          this.templateEditor.mode = 'structured'
          this.templateEditor.regions = parsed.regions
        }
      }
    },

    expandDefaultLetterSection() {
      const first = this.includedOrderedLetterSections[0]
      this.activeLetterSection = first ? first.sectionKey : ''
    },

    resetForm() {
      this.form = emptyForm()
      this.isEditing = false
      this.isReplicating = false
      this.editingId = null
      this.formLoading = false
      this.siteNameManuallyEdited = false
      this._syncingSiteName = false
      this.wizardStep = 0
      this.maxWizardStepReached = 0
      this.templateEditorReady = false
      this.editorMountKey = 0
      this.activeLetterSection = ''
      this.showLetterSettingsModal = false
      this.templateEditor = {
        mode: 'empty',
        fields: emptyTemplateFields(),
        regions: null,
      }
      this.salaryParseWarning = ''
      this.salaryEditor = emptySalaryStructureData()
      this.activeSalaryStructureIndex = 0
      this.syncingSalary = false
    },

    findDuplicateConfig(siteCode, jobTitle, excludeId = null) {
      const code = String(siteCode || '').trim().toLowerCase()
      const title = String(jobTitle || '').trim().toLowerCase()
      if (!code || !title) return null
      return this.configs.find(c => {
        if (excludeId != null && c.id === excludeId) return false
        return (
          String(c.site_code || '').trim().toLowerCase() === code
          && String(c.jobTitle || '').trim().toLowerCase() === title
        )
      }) || null
    },

    buildAssembledHtmlTemplate() {
      const templateEditor = { ...this.templateEditor }
      if (templateEditor.mode !== 'raw') {
        const fields = { ...(templateEditor.fields || {}) }
        fields.salaryStructure = buildSalaryTableHtml(this.salaryEditor)
        templateEditor.fields = fields
        this.templateEditor.fields.salaryStructure = fields.salaryStructure
      }
      const metaDrafts = this.existingMetadata
        && this.existingMetadata.offerSectionDrafts
        && typeof this.existingMetadata.offerSectionDrafts === 'object'
        ? this.existingMetadata.offerSectionDrafts
        : null
      const htmlTemplate = buildOfferGenerationHtml({
        htmlTemplate: this.form.htmlTemplate,
        templateEditor,
        chromeOptions: this.buildChromeOptionsFromFields(),
        // Same draft restore as live preview — Policies ON + empty/broken field.
        offerSectionDrafts: metaDrafts || extractOfferSectionDrafts(templateEditor.fields || {}),
      })
      this.form.htmlTemplate = htmlTemplate
      return htmlTemplate
    },

    /** Snapshot editor state → generation HTML → sample-filled srcdoc (shared with PDF). */
    buildLivePreviewDocumentHtml() {
      const fields = { ...(this.templateEditor.fields || {}) }
      if (this.templateEditor.mode !== 'raw') {
        fields.salaryStructure = buildSalaryTableHtml(this.salaryEditor)
      }
      const metaDrafts = this.existingMetadata
        && this.existingMetadata.offerSectionDrafts
        && typeof this.existingMetadata.offerSectionDrafts === 'object'
        ? this.existingMetadata.offerSectionDrafts
        : null
      const draftsUsed = metaDrafts || extractOfferSectionDrafts(fields)
      const generationHtml = buildOfferGenerationHtml({
        htmlTemplate: this.form.htmlTemplate,
        templateEditor: {
          mode: this.templateEditor.mode,
          fields,
          regions: this.templateEditor.regions,
        },
        chromeOptions: {
          ...this.buildChromeOptionsFromFields(),
          // Preview-only: do not persist into saved HTML via syncTemplateFromFields.
          // Without this, Closing-OFF stamp reapply leaves token-only imgs and the
          // live preview looks unstamped even when Branding → Signature is loaded.
          signatureSrc: (this.form.images && this.form.images.signature) || '',
          signature: (this.form.images && this.form.images.signature) || '',
          images: {
            signature: (this.form.images && this.form.images.signature) || '',
          },
        },
        // Safeguard: Policies ON + empty field but draft still has content.
        offerSectionDrafts: draftsUsed,
      })
      if (!generationHtml) return ''
      return buildOfferPreviewDocumentHtml(generationHtml, {
        jobTitle: this.form.jobTitle,
        location: this.form.location,
        images: this.form.images,
        mode: 'screen',
      })
    },

    /** Sync Quill fields into HTML and compute srcdoc (same path as a section toggle). */
    hydrateAndPrimeLivePreview() {
      this.templateEditorReady = true
      this.syncTemplateFromFields()
      this.refreshLivePreview(true)
    },

    scheduleLivePreviewRefresh(immediate = false) {
      if (!this.showFormModal || this.formLoading) return
      clearTimeout(this.previewRefreshTimer)
      if (immediate) {
        this.$nextTick(() => this.refreshLivePreview())
        return
      }
      this.previewUpdating = true
      this.previewRefreshTimer = setTimeout(() => {
        this.refreshLivePreview()
      }, 300)
    },

    refreshLivePreview(force = false) {
      if (!this.showFormModal || (this.formLoading && !force)) {
        this.previewUpdating = false
        return
      }
      try {
        if (!this.hasPreviewContent) {
          if (this.livePreviewSrcdoc !== this.emptyPreviewSrcdoc) {
            this.livePreviewSrcdoc = this.emptyPreviewSrcdoc
          }
          return
        }
        const next = this.buildLivePreviewDocumentHtml()
        // Keep iframe mounted; only touch srcdoc when HTML actually changes.
        if (next && next !== this.livePreviewSrcdoc) {
          this.livePreviewSrcdoc = next
        } else if (!next && this.livePreviewSrcdoc !== this.emptyPreviewSrcdoc) {
          this.livePreviewSrcdoc = this.emptyPreviewSrcdoc
        }
      } finally {
        this.previewUpdating = false
      }
    },

    clearLivePreview() {
      clearTimeout(this.previewRefreshTimer)
      this.previewRefreshTimer = null
      this.previewUpdating = false
      this.livePreviewSrcdoc = this.emptyPreviewSrcdoc
    },

    buildPayload({ isCreate = false } = {}) {
      const htmlTemplate = this.buildAssembledHtmlTemplate()
      const offerSections = normalizeOfferSections(
        this.templateEditor.fields && this.templateEditor.fields.offerSections,
      )
      const offerSectionOrder = normalizeOfferSectionOrder(
        this.templateEditor.fields && this.templateEditor.fields.offerSectionOrder,
      )
      const existing = (this.existingMetadata && typeof this.existingMetadata === 'object')
        ? this.existingMetadata
        : {}
      const existingHtml = (existing.html && typeof existing.html === 'object')
        ? existing.html
        : {}
      // Preserve previously saved branding when the form field is blank (e.g. list
      // row lacked images but GET-by-id metadata still has watermark/logo).
      const existingImages = extractOfferImagesFromMetadata(existing)
      const logoValue = (this.form.images && this.form.images.logo)
        || existingImages.logo
        || ''
      const images = {
        header: (this.form.images && this.form.images.header) || existingImages.header || '',
        watermark: (this.form.images && this.form.images.watermark) || existingImages.watermark || '',
        signature: (this.form.images && this.form.images.signature) || existingImages.signature || '',
        // Expected DB shape uses companyLogo; letter/PDF code also reads logo
        companyLogo: logoValue,
        logo: logoValue,
      }
      Object.keys(images).forEach(key => {
        if (!images[key]) delete images[key]
      })

      // Canonical metadata shape (version, migratedFrom, images, html.en) plus
      // editor-only keys (offerSections / drafts) required for show/hide.
      const metadata = {
        ...existing,
        version: existing.version || '1.0',
        migratedFrom: existing.migratedFrom || 'JSON files',
        images,
        html: {
          ...existingHtml,
          en: htmlTemplate || '',
        },
        offerSections,
        offerSectionDrafts: extractOfferSectionDrafts(this.templateEditor.fields || {}),
        offerSectionOrder,
        offerSalaryComponentOrder: (() => {
          const normalized = normalizeSalaryStructureData(this.salaryEditor)
          const allRows = (normalized.structures || []).reduce(
            (acc, block) => acc.concat(block.rows || []),
            [],
          )
          return normalizeOfferSalaryComponentOrder(
            extractOfferSalaryComponentOrder(allRows),
            allRows,
          )
        })(),
        offerLayout: normalizeOfferLayout(
          this.templateEditor.fields && this.templateEditor.fields.offerLayout,
        ),
        offerSignaturePages: normalizeOfferSignatureLocations(
          (this.templateEditor.fields && this.templateEditor.fields.offerSignaturePages)
            || (this.templateEditor.fields && this.templateEditor.fields.offerSignaturePlacement),
        ),
        // Keep full multi-select list on both keys (legacy readers accept string|string[]).
        // Do NOT collapse to a single primary via normalizeOfferSignaturePlacement — that
        // dropped withClosing when lastPage was also selected (PDF 31 dual-stamp).
        offerSignaturePlacement: normalizeOfferSignatureLocations(
          (this.templateEditor.fields && this.templateEditor.fields.offerSignaturePages)
            || (this.templateEditor.fields && this.templateEditor.fields.offerSignaturePlacement),
        ),
        offerEmployeeSignConfig: normalizeOfferEmployeeSignConfig(
          (this.templateEditor.fields && this.templateEditor.fields.offerEmployeeSignConfig)
            || (this.templateEditor.fields && this.templateEditor.fields.offerEmployeeSignPlacement),
        ),
        offerEmployeeSignPlacement: normalizeOfferEmployeeSignConfig(
          (this.templateEditor.fields && this.templateEditor.fields.offerEmployeeSignConfig)
            || (this.templateEditor.fields && this.templateEditor.fields.offerEmployeeSignPlacement),
        ),
      }

      const configuration = { jobTitle: this.form.jobTitle.trim() }
      const payload = {
        site_code: this.form.site_code.trim(),
        site_name: this.form.site_name.trim(),
        client: this.form.client.trim() || null,
        project_type: this.form.project_type.trim() || null,
        location: this.form.location.trim() || null,
        configuration: JSON.stringify(configuration),
        metadata,
        is_active: this.form.is_active,
      }

      const userId = this.getCurrentUserEmpId()
      if (userId != null && userId !== '') {
        payload.updated_by = userId
        if (isCreate) {
          payload.created_by = userId
        }
      }

      return payload
    },

    validatePayloadBeforeSave(payload) {
      const tooLong = [
        ['Site Code', payload.site_code],
        ['Site Name', payload.site_name],
        ['Client', payload.client],
        ['Project Type', payload.project_type],
        ['Location', payload.location],
      ].find(([, value]) => value && value.length > MAX_OFFER_CONFIG_FIELD_LENGTH)

      if (tooLong) {
        return `${tooLong[0]} must be ${MAX_OFFER_CONFIG_FIELD_LENGTH} characters or fewer.`
      }

      const htmlEn = payload.metadata && payload.metadata.html && payload.metadata.html.en
      if (this.templateEditor.mode !== 'raw') {
        const fields = this.templateEditor.fields || {}
        const introVisible = isSectionVisible(fields, 'intro')
        const bodyVisible = isSectionVisible(fields, 'body')
        const introSnippet = plainTextSnippet(fields.intro)
        const bodySnippet = plainTextSnippet(fields.body)
        // Hidden sections are omitted from HTML on purpose (drafts stay in metadata).
        if ((introVisible && introSnippet.length >= 12) || (bodyVisible && bodySnippet.length >= 12)) {
          if (!htmlEn) {
            return 'Letter content was not included in the save payload. Please try again or use Advanced: Edit full HTML.'
          }
        }
        if (introVisible && introSnippet.length >= 12 && !htmlContainsPlainSnippet(htmlEn, introSnippet)) {
          return 'Opening paragraph changes could not be written into the template HTML. Use Advanced: Edit full HTML, or contact support.'
        }
        if (bodyVisible && bodySnippet.length >= 12 && !htmlContainsPlainSnippet(htmlEn, bodySnippet)) {
          const repaired = repairOfferBodyAfterChrome(htmlEn, fields)
          if (htmlContainsPlainSnippet(repaired, bodySnippet)) {
            payload.metadata.html.en = repaired
          } else {
            return 'Employment policies changes could not be written into the template HTML. Use Advanced: Edit full HTML, or contact support.'
          }
        }
      }

      const payloadBytes = estimateJsonPayloadBytes(payload)
      if (payloadBytes > MAX_OFFER_CONFIG_PAYLOAD_BYTES) {
        const sizeMb = (payloadBytes / (1024 * 1024)).toFixed(1)
        return `Configuration is too large (${sizeMb} MB; limit 2 MB). Remove or compress uploaded images (logo, header, watermark) and try again.`
      }

      return ''
    },

    letterContentPersistedInRecord(record) {
      if (this.templateEditor.mode === 'raw') return true
      if (!record) return false
      const savedHtml = extractOfferHtmlFromMetadata(record.metadata)
      const fields = this.templateEditor.fields || {}
      const introSnippet = plainTextSnippet(fields.intro)
      const bodySnippet = plainTextSnippet(fields.body)
      if (
        isSectionVisible(fields, 'intro')
        && introSnippet.length >= 12
        && !htmlContainsPlainSnippet(savedHtml, introSnippet)
      ) {
        return false
      }
      if (
        isSectionVisible(fields, 'body')
        && bodySnippet.length >= 12
        && !htmlContainsPlainSnippet(savedHtml, bodySnippet)
      ) {
        return false
      }
      return true
    },

    async saveConfig() {
      if (this.saving) return
      if (!this.canManage) {
        this.toast('You do not have permission to save offer templates.', 'danger')
        return
      }
      if (!this.isFormValid) {
        this.toast('Site code, site name, and job title are required.', 'warning')
        return
      }

      if (this.templateEditor.mode !== 'raw') {
        this.applySalaryEditorToFields()
      }
      // Always assemble from current Quill fields before PUT/POST.
      this.templateEditorReady = true
      this.syncTemplateFromFields()

      // Capture before any await / modal reset — edit must never fall through to POST.
      const editingId = this.editingId != null ? this.editingId : this.form.id
      const isUpdate = !!(this.isEditing || editingId)

      this.saving = true
      try {
        const payload = this.buildPayload({ isCreate: !isUpdate })
        const validationError = this.validatePayloadBeforeSave(payload)
        if (validationError) {
          this.toast(validationError, 'danger')
          return
        }

        if (isUpdate) {
          if (editingId == null || editingId === '') {
            this.toast(
              'Cannot update: missing record id. Close this dialog and use Edit again.',
              'danger',
            )
            return
          }
        } else {
          const dup = this.findDuplicateConfig(payload.site_code, this.form.jobTitle)
          if (dup) {
            const confirmed = await this.$bvModal.msgBoxConfirm(
              `A template already exists for site "${payload.site_code}" with job title "${this.form.jobTitle.trim()}" (id=${dup.id}, ${dup.site_name || 'unnamed'}). Creating another will look like a duplicate. Continue?`,
              {
                title: 'Possible duplicate template',
                okTitle: 'Create anyway',
                cancelTitle: 'Cancel',
                okVariant: 'warning',
              },
            )
            if (!confirmed) return
          }
        }

        const payloadBytes = estimateJsonPayloadBytes(payload)
        console.info('[OfferConfigure] save payload', {
          bytes: payloadBytes,
          mode: isUpdate ? 'update' : 'create',
          id: editingId,
          site_code: payload.site_code,
          configurationType: typeof payload.configuration,
          metadataType: typeof payload.metadata,
          htmlBytes: payload.metadata?.html?.en?.length || 0,
          imageKeys: Object.keys(payload.metadata?.images || {}),
        })

        let res
        if (isUpdate) {
          res = await siteConfigService.updateSiteById(editingId, payload)
        } else {
          res = await siteConfigService.createSite(payload)
        }

        if (!res || res.status !== 'OK') {
          const msg = (res && res.message) || 'Save failed'
          this.toast(msg, 'danger')
          return
        }

        // Confirm letter HTML actually landed (response or fresh GET by id).
        let persistedRecord = res.data
        const savedId = (persistedRecord && persistedRecord.id != null)
          ? persistedRecord.id
          : editingId
        if (!this.letterContentPersistedInRecord(persistedRecord) && savedId != null) {
          const verify = await siteConfigService.getSiteById(savedId)
          if (verify && verify.status === 'OK' && verify.data) {
            persistedRecord = verify.data
          }
        }
        if (!this.letterContentPersistedInRecord(persistedRecord)) {
          this.toast(
            'Server accepted the save, but letter content was not stored. The template was not updated — try Advanced HTML or contact support.',
            'danger',
          )
          return
        }

        siteManager.clearCache()
        this.toast(isUpdate ? 'Configuration updated' : 'Configuration created')
        this.showFormModal = false
        await this.loadConfigs()
      } catch (e) {
        const message = e?.response?.data?.message || e.message || 'Save failed'
        this.toast(message, 'danger')
      } finally {
        this.saving = false
      }
    },

    confirmDelete(item) {
      if (!this.canManage) return
      this.$bvModal
        .msgBoxConfirm(
          `Delete config for "${item.site_name}" (${item.jobTitle || 'no job title'})?`,
          {
            title: 'Confirm Delete',
            okVariant: 'danger',
            okTitle: 'Delete',
            cancelTitle: 'Cancel',
          },
        )
        .then(confirmed => {
          if (confirmed) this.deleteConfig(item)
        })
    },

    toggleTemplateStatus(item) {
      if (!this.canManage) return
      const nextActive = !item.is_active
      this.$bvModal
        .msgBoxConfirm(
          `${nextActive ? 'Activate' : 'Deactivate'} template "${item.site_name}" (${item.jobTitle || 'no job title'})?`,
          {
            title: nextActive ? 'Activate template' : 'Deactivate template',
            okVariant: nextActive ? 'success' : 'warning',
            okTitle: nextActive ? 'Set Active' : 'Set Inactive',
            cancelTitle: 'Cancel',
          },
        )
        .then(confirmed => {
          if (confirmed) this.applyTemplateStatus(item, nextActive)
        })
    },

    async applyTemplateStatus(item, isActive) {
      if (item.id == null) {
        this.toast('Cannot update status: missing record id.', 'danger')
        return
      }
      try {
        const res = await siteConfigService.updateSiteById(item.id, { is_active: isActive })
        if (res.status !== 'OK') {
          this.toast(res.message || 'Failed to update template status', 'danger')
          return
        }
        siteManager.clearCache()
        const idx = this.configs.findIndex(c => c.id === item.id)
        if (idx !== -1) {
          this.$set(this.configs[idx], 'is_active', isActive)
        }
        this.toast(isActive ? 'Template activated' : 'Template deactivated')
      } catch (e) {
        this.toast(e.message || 'Failed to update template status', 'danger')
      }
    },

    async deleteConfig(item) {
      if (!this.canManage) return
      const res = await siteConfigService.deleteSiteById(item.id)
      if (res.status === 'OK') {
        siteManager.clearCache()
        this.toast('Configuration deleted')
        await this.loadConfigs()
      } else {
        this.toast(res.message || 'Delete failed', 'danger')
      }
    },

    onImageUpload(event, key) {
      const file = event?.target?.files?.[0]
      if (!file) return
      const reader = new FileReader()
      reader.onload = e => {
        this.$set(this.form.images, key, e.target.result)
      }
      reader.readAsDataURL(file)
    },

    async previewPdf() {
      if (this.templateEditor.mode !== 'raw') {
        this.applySalaryEditorToFields()
      }
      this.syncTemplateFromFields()
      const rawHtml = this.buildAssembledHtmlTemplate()
      if (!rawHtml) return
      // Sample-fill only — keep page-breaks + placement markers (not screen page cards)
      // so PDF chrome still honors stamp / employee-line settings.
      const html = buildOfferPreviewDocumentHtml(rawHtml, {
        jobTitle: this.form.jobTitle,
        location: this.form.location,
        images: this.form.images,
        mode: 'pdf',
      }) || rawHtml
      this.previewing = true
      try {
        const filename = `preview_${this.form.site_code || 'offer'}.pdf`
        const chromeOpts = this.buildChromeOptionsFromFields()
        const result = await offerLetterService.generatePDFFromHTML(
          html,
          filename,
          undefined,
          undefined,
          {
            preview: true,
            offerSignaturePlacement: chromeOpts.offerSignaturePlacement,
            offerEmployeeSignPlacement: chromeOpts.offerEmployeeSignPlacement,
            offerEmployeeSignConfig: chromeOpts.offerEmployeeSignPlacement,
            signature: (this.form.images && this.form.images.signature) || '',
            images: this.form.images,
          },
        )
        // Production returns data.base64 (service also normalizes to pdf / pdfBase64).
        const pdfBase64 = result?.pdfBase64 || result?.pdf
          || result?.data?.base64 || result?.data?.pdfBase64 || result?.data?.pdf
        if (pdfBase64) {
          const blob = this.base64ToBlob(pdfBase64, 'application/pdf')
          const url = URL.createObjectURL(blob)
          window.open(url, '_blank')
        } else {
          this.toast('PDF generated but no preview data returned', 'warning')
        }
      } catch (e) {
        const data = e?.responseData || e?.response?.data
        let serverMsg = e?.message || 'Unknown error'
        if (data && typeof data === 'object') {
          serverMsg = data.message || data.error || data.msg || serverMsg
          try {
            const full = JSON.stringify(data)
            if (full && full !== '{}' && !serverMsg.includes(full)) {
              serverMsg = `${serverMsg} (${full})`
            }
          } catch (err) { /* ignore */ }
        } else if (typeof data === 'string' && data.trim()) {
          serverMsg = data.trim()
        }
        this.toast(`Preview failed: ${serverMsg}`, 'danger')
      } finally {
        this.previewing = false
      }
    },

    base64ToBlob(base64, mime) {
      const raw = base64.includes(',') ? base64.split(',')[1] : base64
      const bytes = atob(raw)
      const arr = new Uint8Array(bytes.length)
      for (let i = 0; i < bytes.length; i += 1) {
        arr[i] = bytes.charCodeAt(i)
      }
      return new Blob([arr], { type: mime })
    },
  },
}
</script>

<style scoped>
.offer-configure {
  --oc-teal: #00bab0;
  --oc-teal-soft: rgba(0, 186, 176, 0.12);
  --oc-border: #e5e5e5;
  --oc-muted: #6e6b7b;
  --oc-surface: #f8f9fb;
  margin-top: -0.85rem;
}

.offer-configure .offer-more-toggle {
  width: 2rem;
  height: 2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.oc-touch-btn {
  min-height: 2.375rem;
  font-weight: 600;
}

.oc-list-card {
  background: #fff;
}

/* Wizard modal */
.oc-wizard-header {
  position: sticky;
  top: 0;
  z-index: 5;
  background: #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
}

.oc-edit-banner {
  border-radius: 0.4rem;
  background: var(--oc-teal-soft) !important;
  border-color: rgba(0, 186, 176, 0.35) !important;
  color: #0f766e !important;
}

.oc-branding-badge {
  font-size: 0.625rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.oc-section-chip-row {
  padding-top: 0.35rem;
  border-top: 1px solid var(--oc-border);
}

.oc-section-chip {
  font-weight: 500;
  font-size: 0.6875rem;
}

.oc-section-chip-state {
  opacity: 0.75;
  text-transform: uppercase;
  font-size: 0.625rem;
  margin-left: 0.2rem;
}

.oc-wizard-body {
  min-height: 360px;
  padding-bottom: 6rem;
  background: #fafbfc;
}

.oc-live-preview-sticky {
  position: sticky;
  top: 0.5rem;
}

.oc-live-preview-scroll {
  max-height: calc(100vh - 12rem);
  overflow-y: auto;
  padding-right: 0.15rem;
}

.oc-wizard-form-col {
  min-width: 0;
}

.oc-form-loading {
  min-height: 360px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.oc-wizard-footer {
  padding: 0.25rem 0;
}

.oc-save-btn {
  min-width: 10rem;
  box-shadow: 0 2px 6px rgba(0, 186, 176, 0.3);
}

@media (max-width: 991.98px) {
  .oc-wizard-footer .oc-touch-btn {
    min-height: 2.5rem;
    flex: 1 1 auto;
  }
}
</style>

<style lang="scss">
/* Modal is portaled to body — inherit theme tokens here too. */
.offer-configure-modal {
  --oc-teal: #00bab0;
  --oc-teal-soft: rgba(0, 186, 176, 0.12);
  --oc-border: #e5e5e5;
  --oc-muted: #6e6b7b;
  --oc-surface: #f8f9fb;
  --oc-wizard-footer-height: 5.5rem;

  /* Nearly full-viewport shell — overrides Bootstrap xl (~1140px) cap */
  .offer-configure-modal-dialog,
  .modal-dialog {
    width: 96vw;
    max-width: 96vw;
    height: calc(100vh - 1.5rem);
    max-height: calc(100vh - 1.5rem);
    margin: 0.75rem auto;
  }

  .offer-configure-modal-content,
  .modal-content {
    height: 100%;
    max-height: 100%;
  }

  .modal-body {
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    flex: 1 1 auto;
    min-height: 0;
  }

  .oc-wizard-body {
    padding-left: 1.25rem;
    padding-right: 1.25rem;
  }

  .oc-wizard-split > [class*='col-'] {
    padding-left: 0.85rem;
    padding-right: 0.85rem;
  }

  .oc-live-preview-sticky {
    position: sticky;
    top: 0.5rem;
  }

  .oc-live-preview-scroll {
    max-height: calc(100vh - 11rem);
    overflow-y: auto;
    padding-right: 0.25rem;
  }

  @media (max-width: 1199.98px) {
    .offer-configure-modal-dialog,
    .modal-dialog {
      width: 98vw;
      max-width: 98vw;
      height: calc(100vh - 1rem);
      max-height: calc(100vh - 1rem);
      margin: 0.5rem auto;
    }
  }

  .modal-footer {
    background: #fff;
    border-top: 1px solid #eef0f3;
    padding: 0.85rem 1.25rem;
    position: sticky;
    bottom: 0;
    z-index: 5;
    flex-shrink: 0;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.04);
  }

  .modal-header {
    border-bottom: 1px solid #eef0f3;
    padding: 0.85rem 1.25rem;
  }

  .modal-title {
    font-weight: 700;
    font-size: 1.05rem;
  }

  /* Shared step header — wizard steps live inside this portaled modal */
  .oc-step-header {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .oc-step-header--compact {
    align-items: center;
    gap: 0.5rem;
  }

  .oc-step-header__icon {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 0.55rem;
    background: var(--oc-teal-soft);
    color: var(--oc-teal);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .oc-step-header__icon--compact {
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 0.4rem;
  }

  .oc-step-header__body {
    min-width: 0;
    flex: 1 1 auto;
  }

  .oc-step-header__title {
    font-size: 1.05rem;
    font-weight: 700;
    color: #2c2c2c;
    margin-bottom: 0.25rem;
    letter-spacing: -0.01em;
  }

  .oc-step-header--compact .oc-step-header__title {
    font-size: 0.9375rem;
    margin-bottom: 0;
    display: inline;
  }

  .oc-step-header__hint {
    font-size: 0.8125rem;
    color: var(--oc-muted);
    line-height: 1.35;
  }

  .oc-step-header--compact .oc-step-header__hint {
    display: inline;
  }

  .oc-step-header__lead {
    font-size: 0.8125rem;
    color: var(--oc-muted);
    line-height: 1.45;
    max-width: 40rem;
  }

  /* Quill editor — content left (col-9), formatting toolbar right (col-3) */
  .offer-quill-wrap {
    margin-bottom: 0.25rem;
  }

  .offer-quill-format-hint {
    display: flex;
    align-items: flex-start;
    line-height: 1.4;
    margin-bottom: 0.5rem;
  }

  .offer-quill-grid {
    background: #fff;
    border: 1px solid #eef0f3;
    border-radius: 0.4rem;
    overflow: visible;
  }

  .offer-quill-editor-col {
    min-width: 0;
    overflow: hidden;
    border-radius: 0.4rem 0 0 0.4rem;
  }

  @media (max-width: 767.98px) {
    .offer-quill-editor-col {
      border-radius: 0.4rem 0.4rem 0 0;
    }
  }

  .offer-quill-editor {
    background: #fff;
    border: none;
    border-radius: 0;
  }

  /* Toolbar is moved into .offer-quill-toolbar-slot on @ready */
  .offer-quill-editor > .ql-toolbar {
    display: none !important;
  }

  .offer-quill-editor .ql-container {
    border: none;
    font-size: 0.875rem;
    min-height: 140px;
  }

  .offer-quill-editor--closing .ql-container {
    min-height: 100px;
  }

  .offer-quill-toolbar-col {
    border-left: 1px solid #eef0f3;
    background: #f8f9fb;
    overflow: visible;
    position: relative;
    z-index: 2;
    border-radius: 0 0.4rem 0.4rem 0;
  }

  .offer-quill-toolbar-slot {
    position: sticky;
    top: 0;
    align-self: flex-start;
    overflow: visible;
  }

  .offer-quill-toolbar-slot .ql-toolbar {
    border: none;
    background: transparent;
    padding: 0.65rem 0.55rem;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 0;
    overflow: visible;
  }

  .offer-quill-toolbar-slot .ql-toolbar .ql-formats {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    margin-right: 0 !important;
    padding: 0.45rem 0;
    border-bottom: 1px solid #e3e6ea;
    overflow: visible;
  }

  .offer-quill-toolbar-slot .ql-toolbar .ql-formats:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  .offer-quill-toolbar-slot .ql-toolbar .ql-formats[data-group-label]::before {
    content: attr(data-group-label);
    display: block;
    width: 100%;
    font-size: 0.625rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #6e6b7b;
    margin-bottom: 0.3rem;
  }

  .offer-quill-toolbar-slot .ql-toolbar .ql-picker.ql-font,
  .offer-quill-toolbar-slot .ql-toolbar .ql-picker.ql-size {
    width: 100%;
    float: none;
  }

  .offer-quill-toolbar-slot .ql-toolbar .ql-picker {
    overflow: visible;
  }

  .offer-quill-toolbar-slot .ql-toolbar .ql-picker-options {
    z-index: 20;
    max-height: 14rem;
    overflow-y: auto;
    white-space: normal;
  }

  /* Custom font/size whitelists use data-value, not Quill's default class names */
  .offer-quill-toolbar-slot .ql-toolbar.ql-snow .ql-picker.ql-font .ql-picker-label[data-value]::before,
  .offer-quill-toolbar-slot .ql-toolbar.ql-snow .ql-picker.ql-font .ql-picker-item[data-value]::before,
  .offer-quill-toolbar-slot .ql-toolbar.ql-snow .ql-picker.ql-font .ql-picker-label[data-label]:not([data-label=''])::before,
  .offer-quill-toolbar-slot .ql-toolbar.ql-snow .ql-picker.ql-font .ql-picker-item[data-label]:not([data-label=''])::before {
    content: attr(data-label);
  }

  .offer-quill-toolbar-slot .ql-toolbar.ql-snow .ql-picker.ql-size .ql-picker-label[data-value]::before,
  .offer-quill-toolbar-slot .ql-toolbar.ql-snow .ql-picker.ql-size .ql-picker-item[data-value]::before,
  .offer-quill-toolbar-slot .ql-toolbar.ql-snow .ql-picker.ql-size .ql-picker-label[data-label]:not([data-label=''])::before,
  .offer-quill-toolbar-slot .ql-toolbar.ql-snow .ql-picker.ql-size .ql-picker-item[data-label]:not([data-label=''])::before {
    content: attr(data-label);
  }

  .offer-quill-toolbar-slot .ql-toolbar.ql-snow .ql-picker.ql-font .ql-picker-item[data-value='Calibri']::before {
    font-family: Calibri, 'Segoe UI', sans-serif;
  }

  .offer-quill-toolbar-slot .ql-toolbar.ql-snow .ql-picker.ql-font .ql-picker-item[data-value='Arial']::before {
    font-family: Arial, Helvetica, sans-serif;
  }

  .offer-quill-toolbar-slot .ql-toolbar.ql-snow .ql-picker.ql-font .ql-picker-item[data-value='Georgia']::before {
    font-family: Georgia, 'Times New Roman', serif;
  }

  .offer-quill-toolbar-slot .ql-toolbar.ql-snow .ql-picker.ql-font .ql-picker-item[data-value='Times New Roman']::before {
    font-family: 'Times New Roman', Times, serif;
  }

  .offer-quill-toolbar-slot .ql-toolbar button {
    width: 2rem;
    height: 2rem;
  }

  .offer-quill-toolbar-slot .ql-toolbar .ql-picker-label {
    min-height: 2rem;
    display: flex;
    align-items: center;
  }

  .offer-quill-toolbar-slot .ql-picker-label::before {
    vertical-align: top;
  }

  @media (max-width: 767.98px) {
    .offer-quill-format-hint::after {
      content: ' On small screens, tools appear below the editor.';
    }

    .offer-quill-toolbar-col {
      border-left: none;
      border-top: 1px solid #eef0f3;
      border-radius: 0 0 0.4rem 0.4rem;
    }

    .offer-quill-toolbar-slot {
      position: static;
    }

    .offer-quill-toolbar-slot .ql-toolbar {
      flex-direction: row;
      flex-wrap: wrap;
    }

    .offer-quill-toolbar-slot .ql-toolbar .ql-formats {
      flex: 1 1 auto;
      min-width: 7rem;
      border-bottom: none;
      border-right: 1px solid #e3e6ea;
      padding: 0.35rem 0.45rem;
    }

    .offer-quill-toolbar-slot .ql-toolbar .ql-formats:last-child {
      border-right: none;
    }
  }

  /* Image preview thumb */
  .offer-configure-thumb {
    max-width: 100%;
    max-height: 120px;
    object-fit: contain;
    border-radius: 0.25rem;
  }

}
</style>
